'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Environment {
    label: string;
    verb: string;
    img: string;
    alt: string;
    color: string;
    textColor: string;
}

const environments: Environment[] = [
    {
        label: 'Studio',
        verb: 'Create',
        img: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80',
        alt: 'Musician teaching guitar in a recording studio, dim studio lighting, dark acoustic panels, atmospheric glow from monitors',
        color: 'from-purple/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Computer',
        verb: 'Code',
        img: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
        alt: 'Developer teaching programming, bright screen glow in dim room, code visible on dark screen, atmospheric blue lighting',
        color: 'from-primary/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Classroom',
        verb: 'Teach',
        img: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&q=80',
        alt: 'Teacher explaining mathematics on a whiteboard, dim classroom with focused overhead lighting, dark window backgrounds',
        color: 'from-green/60 to-transparent',
        textColor: 'text-white'
    }];

interface LiveSession {
    title: string;
    level: string;
    format: string;
    schedule: string;
    category: string;
    categoryColor: string;
    img: string;
    alt: string;
    rating: number;
    enrolled: string;
    instructor: string;
    type: 'live-slot' | 'bootcamp' | 'program';
}

const sessions: LiveSession[] = [
    {
        title: 'Nigerian Cooking Masterclass',
        level: 'Beginner',
        format: 'Live Sessions',
        schedule: 'Tue & Thu · 6pm',
        category: 'Skills',
        categoryColor: 'bg-orange/10 text-orange border-orange/20',
        img: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=500&q=80',
        alt: 'Nigerian cooking class with vibrant spices and ingredients on a bright wooden kitchen counter',
        rating: 4.9,
        enrolled: '3.2k',
        instructor: 'Chef Amaka',
        type: 'live-slot'
    },
    {
        title: 'Full-Stack Web Development',
        level: 'Intermediate',
        format: 'Bootcamp',
        schedule: '8 weeks · Mon–Fri',
        category: 'Tech',
        categoryColor: 'bg-primary/10 text-primary border-primary/20',
        img: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=500&q=80',
        alt: 'Developer coding on laptop in bright modern workspace, clean desk with multiple screens',
        rating: 4.8,
        enrolled: '5.1k',
        instructor: 'Tunde Okafor',
        type: 'bootcamp'
    },
    {
        title: 'Professional Hairdressing',
        level: 'Beginner',
        format: 'Live Sessions',
        schedule: 'Sat & Sun · 10am',
        category: 'Skills',
        categoryColor: 'bg-coral/10 text-coral border-coral/20',
        img: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=500&q=80',
        alt: 'Hairstylist demonstrating braiding technique in a bright modern salon with natural light',
        rating: 4.9,
        enrolled: '2.8k',
        instructor: 'Ngozi Eze',
        type: 'live-slot'
    },
    {
        title: 'Mobile Photography',
        level: 'Beginner',
        format: 'Bootcamp',
        schedule: '4 weeks · Weekends',
        category: 'Creative',
        categoryColor: 'bg-purple/10 text-purple border-purple/20',
        img: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=500&q=80',
        alt: 'Photographer teaching mobile photography outdoors in bright daylight, colorful city background',
        rating: 4.7,
        enrolled: '4.4k',
        instructor: 'Kemi Adebayo',
        type: 'bootcamp'
    },
    {
        title: 'Fashion Design Fundamentals',
        level: 'Intermediate',
        format: 'Live Sessions',
        schedule: 'Wed & Fri · 4pm',
        category: 'Creative',
        categoryColor: 'bg-purple/10 text-purple border-purple/20',
        img: 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=500&q=80',
        alt: 'Fashion designer teaching sewing and pattern-making in a bright studio with natural light and fabric',
        rating: 4.8,
        enrolled: '1.9k',
        instructor: 'Adaeze Nwosu',
        type: 'live-slot'
    },
    {
        title: 'Digital Marketing Essentials',
        level: 'Beginner',
        format: 'Bootcamp',
        schedule: '6 weeks · Tue & Thu',
        category: 'Business',
        categoryColor: 'bg-green/10 text-green border-green/20',
        img: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=500&q=80',
        alt: 'Marketing professional teaching digital strategy in a bright open-plan office with large windows',
        rating: 4.7,
        enrolled: '6.3k',
        instructor: 'Emeka Obi',
        type: 'bootcamp'
    }];

const typeConfig = {
    'live-slot': { label: 'Live Slot', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
    'bootcamp': { label: 'Bootcamp', color: 'bg-primary/10 text-primary border-primary/20' },
    'program': { label: 'Program', color: 'bg-green/10 text-green border-green/20' },
};

export default function DiscoverSection() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [activeCategory, setActiveCategory] = useState('All');

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.1 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section id="discover" ref={sectionRef} className="relative py-20 md:py-28 bg-background overflow-hidden">
            <div className="absolute inset-0 grid-bg pointer-events-none opacity-60" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] blob-accent opacity-30 translate-x-1/4 translate-y-1/4 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    {/* Header */}

                    <div className="order-1">
                        <div className="max-w-2xl">
                            <div className="reveal mb-4">
                                <span className="pill-tag border border-primary/20 text-primary bg-primary/8 text-xs uppercase tracking-wider">
                                    Discover
                                </span>
                            </div>
                            <h2 className="reveal text-section-title font-extrabold text-foreground delay-100">
                                Find something worth learning.
                            </h2>
                            <p className="reveal text-lg text-muted-foreground font-body leading-relaxed mt-3 max-w-xl delay-200">
                                Browse live sessions, programs and bootcamps run by real instructors. Book a time slot or join a structured bootcamp — every class happens in real time.
                            </p>
                        </div>
                    </div>

                    {/* Right - Photo grid — 3 rotated cards */}
                    <div className="order-2 flex flex-col gap-6">
                        <div className="reveal delay-300">
                            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-x-28 gap-y-10">
                                {sessions.map((session, i) => {
                                    const sessionCardClasses = [
                                        'session-card-1',
                                        'session-card-2',
                                        'session-card-3',
                                        'session-card-4',
                                        'session-card-5',
                                        'session-card-6'
                                    ];

                                    return (
                                        <div
                                            key={session.title}
                                            className={`relative group cursor-pointer w-52 transition-all duration-500 ease-out will-change-transform ${sessionCardClasses[i]}`}
                                            style={{ transitionDelay: `${i * 60}ms` }}>

                                            <div className="glass-light rounded-2xl overflow-hidden shadow-card-lg border border-white/60">
                                                <div className="h-52 overflow-hidden">
                                                    <AppImage
                                                        src={session.img}
                                                        alt={session.alt}
                                                        width={240}
                                                        height={320}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                    />

                                                    <div className="absolute top-3 left-3">
                                                        <span className={`pill-tag border ${session.categoryColor} bg-card`}>
                                                            {session.category}
                                                        </span>
                                                    </div>

                                                    <div className="absolute top-3 right-3">
                                                        <span className={`pill-tag border ${typeConfig[session.type].color} bg-white/90 backdrop-blur-sm text-xs font-bold`}>
                                                            {session.type === 'live-slot' && (
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1 animate-pulse" />
                                                            )}
                                                            {typeConfig[session.type].label}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="p-3">
                                                    <h3 className="font-bold text-foreground text-sm truncate">
                                                        {session.title}
                                                    </h3>

                                                    <p className="text-muted-foreground text-xs mt-0.5 mb-2 truncate">
                                                        {session.level} · {session.instructor}
                                                    </p>

                                                    <div className="flex items-center justify-between pt-2 border-t border-border">
                                                        <div className="flex items-center gap-1.5">
                                                            <svg className="w-4 h-4 text-yellow-400 fill-yellow-400" viewBox="0 0 24 24">
                                                                <path d="M12 17.27L18.18 21l-1.64-7.03L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                                            </svg>
                                                            <span className="text-foreground font-bold text-sm">
                                                                {session.rating}
                                                            </span>
                                                        </div>

                                                        <span className="text-muted-foreground text-xs font-body">
                                                            {session.enrolled} enrolled
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>);

                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Categories bento */}
                <div className="mt-16 md:mt-20">
                    <div className="reveal mb-8 delay-100">
                        <h2 className="text-section-title font-extrabold text-foreground">
                            Whatever you're curious about.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                        {/* Technology — spans 2 cols */}
                        <div className="reveal md:col-span-2 delay-100">
                            <div className="bg-primary rounded-2xl p-6 md:p-8 h-full min-h-[160px] flex flex-col justify-between group card-hover cursor-pointer overflow-hidden relative">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-1/3 -translate-y-1/3" />
                                <div>
                                    <h3 className="text-xl font-bold text-primary-foreground mb-2">Technology</h3>
                                    <p className="text-white/70 text-sm font-body">Programming · AI · Cybersecurity · Web Development</p>
                                </div>
                                <div className="flex items-center gap-2 mt-4">
                                    <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs">95+ programs</span>
                                </div>
                            </div>
                        </div>

                        {/* Creative — 1 col */}
                        <div className="reveal delay-200">
                            <div className="bg-purple rounded-2xl p-6 h-full min-h-[160px] flex flex-col justify-between group card-hover cursor-pointer overflow-hidden relative">
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/4 translate-y-1/4" />
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Creative</h3>
                                    <p className="text-white/70 text-sm font-body">Photography · Design · Music · Video</p>
                                </div>
                                <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs w-fit mt-4">75+ bootcamps</span>
                            </div>
                        </div>

                        {/* Practical Skills */}
                        <div className="reveal delay-100">
                            <div className="bg-accent rounded-2xl p-6 h-full min-h-[160px] flex flex-col justify-between group card-hover cursor-pointer overflow-hidden relative">
                                <div className="absolute top-0 right-0 w-28 h-28 bg-white/10 rounded-full translate-x-1/4 -translate-y-1/4" />
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Practical Skills</h3>
                                    <p className="text-white/70 text-sm font-body">Cooking · Hairdressing · Carpentry · Fashion</p>
                                </div>
                                <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs w-fit mt-4">120+ sessions</span>
                            </div>
                        </div>

                        {/* Business */}
                        <div className="reveal delay-200">
                            <div className="bg-green rounded-2xl p-6 h-full min-h-[160px] flex flex-col justify-between group card-hover cursor-pointer overflow-hidden relative">
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Business</h3>
                                    <p className="text-white/70 text-sm font-body">Entrepreneurship · Marketing · Finance · Sales</p>
                                </div>
                                <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs w-fit mt-4">60+ bootcamps</span>
                            </div>
                        </div>

                        {/* Personal Growth */}
                        <div className="reveal delay-300">
                            <div className="bg-coral rounded-2xl p-6 h-full min-h-[160px] flex flex-col justify-between group card-hover cursor-pointer overflow-hidden relative">
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Personal Growth</h3>
                                    <p className="text-white/70 text-sm font-body">Fitness · Communication · Productivity</p>
                                </div>
                                <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs w-fit mt-4">45+ sessions</span>
                            </div>
                        </div>

                        {/* Academic — spans full width */}
                        <div className="reveal md:col-span-3 delay-200">
                            <div className="bg-card border border-border rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 group card-hover cursor-pointer overflow-hidden relative">
                                <div className="absolute right-0 top-0 bottom-0 w-48 bg-gradient-to-l from-primary/5 to-transparent" />
                                <div>
                                    <h3 className="text-xl font-bold text-foreground mb-1.5">Academic</h3>
                                    <p className="text-muted-foreground text-sm font-body">Mathematics · Sciences · Languages · History — all taught live by qualified teachers</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="pill-tag border border-primary/20 text-primary bg-primary/8 text-xs">240+ programs</span>
                                    <Link
                                        href="/learn"
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold hover:bg-primary/90 shadow-blue transition-all duration-200 group-hover:-translate-y-0.5">

                                        Explore all
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}